<template>
  <div id="model-detail">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsProductDetailBW :organism="brandWindow" />
      <OrganismsFlexibleFeatures
        v-if="flexibleFeatures.item"
        :organism="flexibleFeatures"
      />
      <OrganismsGalleryGrid
        class="pt-24"
        v-if="galleryGrid"
        :organism="galleryGrid"
      />
      <div
        class="px-2 pt-24 pb-12 mx-auto bg-bc-gray view-a-slideshow"
        v-if="viewSlideshow.slideshowEmbed"
      >
        <h2
          class="mb-8 text-2xl text-center md:text-3xl lg:text-5xl theme-text-2xl"
        >
          {{ viewSlideshow.title1 }}
        </h2>
        <div class="pb-16" v-html="viewSlideshow.slideshowEmbed"></div>
        <!--<MoleculesButtons
          :buttons="viewSlideshow.buttons"
          :button-colors="viewSlideshow.buttons.buttonColors"
          class="uppercase"
        />-->
      </div>
      <OrganismsVirtualTour
        id="virtual-tour"
        v-if="virtualTour.virtualTourUrl"
        class="mx-auto my-24 bg-gray-900"
        :organism="virtualTour"
      />
      <OrganismsPopularHomes v-if="popularHomes.items.length" :organism="popularHomes" pageType="model" :dynamicName="modelName" />
      <OrganismsMapWithInfo :organism="mapWithInfo" />
      <OrganismsTextCTA
        id="cta-be-the-first"
        :organism="beTheFirst"
        class="my-16"
      />
      <OrganismsModalForm :modalId="modalId">
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
            Be the First
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Please provide us your information and we will get back to you.
          </p>
        </header>
        <FormsHubSpotForm
          :form="{ id: '767c334f-0893-4757-8fa6-fd4b253bd691' }"
          @submit="isFormSubmitted = true"
        />
      </OrganismsModalForm>
      
      
    </div>
  </div>
</template>

<style>
#model-detail {
  .view-a-slideshow {
    iframe {
      @apply mx-auto w-[1038px] max-w-full h-80 sm:h-[696px] overflow-hidden;
    }
    .buttons {
      @apply justify-center;
    }
  }
  .virtual-tour {
    @apply mt-0 mb-0;
    h2 {
      @apply text-white;
    }
  }

  .popular-homes {
    .product-grid {
      @apply w-full;
    }
  }
}
</style>

<script setup>
const route = useRoute();
import { useAsyncQuery } from "#imports";
import { GET_MODEL } from "~/graphql/GetModel";
// Explicit, stable key so the SSR/ISR payload is reused on hydration instead
// of refetching (the auto key hashes a reactive proxy that differs between the
// Node prerender and the browser). Key off route.params, NOT route.path:
// route.path differs by a trailing slash between the prerender and the browser,
// which would reintroduce the mismatch. See community index.vue for the story.
const { data, pending, error } = await useAsyncQuery({
  query: GET_MODEL,
  variables: { uri: route.path },
  key: `model-detail:${route.params.location}/${route.params.community}/${route.params.model}`,
});
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const loading = pending;

const model = computed(() => {
  return data?.value?.model ?? [];
});

const homes = computed(() => {
  return data?.value?.homes?.nodes ?? [];
});

// Get the model title to filter homes
const modelTitle = computed(() => {
  return model.value?.title;
});

// Filter homes based on the model's collection
const filteredHomes = computed(() => {
  return homes.value.filter(home => home?.qmiHomeDetails?.model?.title === modelTitle.value);
});

//console.log(filteredHomes.value);

const modelName = computed(() => {
  return model?.value?.title;
});

// Shuffle and limit to 3 items
const popularHomes = computed(() => {
  const shuffledHomes = filteredHomes.value.sort(() => 0.5 - Math.random());
  return {
    backgroundColor: "#f0f0f0",
    items: shuffledHomes.slice(0, 3),
  };
});

//console.log("popularHomes", popularHomes);

const computedBreadcrumbs = computed(() => {
  return [
    {
      title:
        model?.value?.modelDetails?.neighborhood?.title +
        ", " +
        model?.value?.modelDetails?.neighborhood?.neighborhood?.city,
      link: model?.value?.modelDetails?.neighborhood?.uri,
    },
    {
      title: "Model Homes",
      link: model?.value?.modelDetails?.neighborhood?.uri + "#model-homes",
    },
    {
      title: model?.value?.title + " Model",
    },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brandWindow = computed(() => {
  return {
    collection:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.collection ??
      model?.value?.collection?.collection ??
      "",
    title: model?.value?.title,
    type: model?.value?.modelDetails?.floorplan?.floorplanDetails?.type ?? "",
    neighborhoodTitle: model?.value?.modelDetails?.neighborhood?.title,
    neighborhoodLink: model?.value?.modelDetails?.neighborhood?.uri,
    bedsMin:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.bedroomsMin ??
      "",
    bedsMax:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.bedroomsMax ??
      "",
    bathsMin:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.bathroomsMin ??
      "",
    bathsMax:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.bathroomsMax ??
      "",
    garageMin:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.garageMin ??
      "",
    garageMax:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.garage ??
      "",
    sqftMin:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.mainSqrFt ?? "",
    sqftMax:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.finishedSqrFt ??
      "",
    priceMin:
      model?.value?.modelDetails?.floorplan?.floorplanDetails?.basePrice ?? "",
    autoplaySlider: true,
    autoplayDelay: 10000,
    sliderImages: model?.value?.modelDetails?.elevations,
    button1: true,
    button1Text: "Request Info",
    link1: "#request-info",
  };
});

const collectionFeatures = computed(() => {
  let parsedJSON = {};
  try {
    parsedJSON = JSON.parse(data?.value?.collectionFeatures);
  } catch (e) {
    // console.log("Invalid collectionFeatures JSON:", e);
    return {};
  }
  return parsedJSON;
});

const flexibleFeatures = computed(() => {
  const collection =
    model?.value?.modelDetails?.floorplan?.floorplanDetails?.collection ??
    model?.value?.collection?.collection ??
    "";
  let collectionTitle = "";
  let featuresList = [];

  switch (true) {
    case collection?.toLowerCase().includes("easyhouse"):
      collectionTitle = "easyHouse®";
      featuresList = collectionFeatures?.value?.easyHouse ?? [];
      break;
    case collection?.toLowerCase().includes("deed-restricted-wc"):
      collectionTitle = "Deed Restricted wee-Cottage®";
      featuresList = collectionFeatures?.value?.deedRestrictedWC ?? [];
      break;
    case collection?.toLowerCase().includes("wee-cottage"):
      collectionTitle = "wee-Cottage®";
      featuresList = collectionFeatures?.value?.weeCottage ?? [];
      break;
    case collection?.toLowerCase().includes("limited-edition"):
      collectionTitle = "Limited Edition";
      featuresList = collectionFeatures?.value?.limitedEdition ?? [];
      break;
    case collection?.toLowerCase().includes("new-beginnings"):
      collectionTitle = "New Beginnings";
      featuresList = collectionFeatures?.value?.newBeginnings ?? [];
      break;
  }

  return {
    title1: collectionTitle + " Features",
    item: featuresList,
  };
});

const galleryGrid = computed(() => {
  if (!Array.isArray(model?.value?.modelDetails?.galleryItems)) {
    return false;
  }
  return {
    gallery: model?.value?.modelDetails?.galleryItems.reduce(
      (imageArray, item) => {
        if (item.type === "image") {
          imageArray.push(item.image);
          return imageArray;
        }
      },
      [],
    ),
  };
});

const virtualTour = computed(() => {
  const collection =
    model?.value?.modelDetails?.floorplan?.floorplanDetails?.collection ??
    model?.value?.collection?.collection ??
    "";
  
  // Format collection name for display
  let collectionDisplay = collection;
  if (collection?.includes("deed-restricted-wc")) {
    collectionDisplay = "Deed Restricted wee-Cottage®";
  }
  
  const collectionSpace = collectionDisplay ? collectionDisplay + " " : "";

  const showButton1 = model?.value?.modelDetails?.floorplan ? true : false;

  return {
    title1: model?.value?.modelDetails?.matterportTourTitle,
    title2: "Tour this " + collectionSpace + " home",
    virtualTourEmbedType: "url",
    virtualTourUrl: model?.value?.modelDetails?.matterportTour,
    button1: showButton1,
    button1Text:
      "View the " + model?.value?.modelDetails?.floorplan?.title + " floorplan",
    link1: model?.value?.modelDetails?.floorplan?.uri,
    buttonColors: ["button-gray"],
  };
});

const viewSlideshow = computed(() => {
  const showButton1 = model?.value?.modelDetails?.floorplan?.floorplanDetails
    ?.pdfEPub
    ? true
    : false;
  return {
    title1: "Step inside the " + model?.value?.title,
    slideshowEmbed: model?.value?.modelDetails?.virtualTour,
    buttons: {
      button1: showButton1,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: model?.value?.modelDetails?.floorplan?.floorplanDetails?.pdfEPub,
      buttonColors: ["button-yellow"],
    },
  };
});

const mapWithInfo = computed(() => {
  return {
    markerTitle: model?.value?.modelDetails?.neighborhood?.title,
    title1: "Stop by for a visit",
    address1: model?.value?.modelDetails?.address1,
    city: model?.value?.modelDetails?.neighborhood?.neighborhood?.city,
    state: model?.value?.modelDetails?.neighborhood?.neighborhood?.state,
    zip: model?.value?.modelDetails?.neighborhood?.neighborhood?.zip,
    latitude: model?.value?.modelDetails?.latitude,
    longitude: model?.value?.modelDetails?.longitude,
    hours: model?.value?.modelDetails?.neighborhood?.neighborhood?.hours,
    phone: model?.value?.modelDetails?.neighborhood?.neighborhood?.phone,
    salesTeamMembers:
      model?.value?.modelDetails?.neighborhood?.neighborhood?.saleTeamMembers,
    legal: model?.value?.modelDetails?.neighborhood?.neighborhood?.legal,
  };
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;

  if (urlHash && oldLoading && !newLoading) {
    await nextTick();
    document.querySelector(urlHash).scrollIntoView(true);
  }
});

const beTheFirst = computed(() => {
  return {
    title1: "Be the First",
    description: `<p>Be the first to know about our Colorado neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>`,
    link1: "#modal-form",
    button1: true,
    button1Text: "Get Updates",
  };
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

const modalId = "BeTheFirst-" + generateRandomString(8);

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModalWithCheck(modalId);
    });
  });
};

onUpdated(() => {
  addTriggers();
});
</script>
